"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { generateSts } from "@/app/_api/generateSts";
import { Avatar } from "@components/core/Avatar";
import { Button } from "@components/core/Button";
import MediaProgressBar from "@components/core/MediaProgressBar";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import Pause20 from "@ui/icons/20x20/pause-20.svg";
import Play20 from "@ui/icons/20x20/play-20.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useContext, useEffect, useRef, useState } from "react";

import { STSContext } from "../STS";

export default function Generate() {
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [playingState, setPlayingState] = useState<"loading" | "paused" | "playing">("paused");
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const { voices, audioRecorderRef, setView, file, setFile } = useContext(STSContext);
  const t = useTranslations("ClientKeys.Globals");

  const getInputType = (file: File | null): "recording" | "file_upload" => {
    if (!file) return "file_upload";
    return file.name.startsWith("recording.") ? "recording" : "file_upload";
  };

  useEffect(() => {
    if (audioRecorderRef.current === null) return;
    const blob = audioRecorderRef.current.getBlob();
    if (blob.size === 0) return;
    const extension = audioRecorderRef.current.getExtension();
    const name = `recording.${extension}`;
    const file = new File([blob], name, {
      type: audioRecorderRef.current.getMimeType(),
    });
    setFile(file);
  }, []);

  async function generate(voiceId: string) {
    if (!audioElementRef.current || !file) return;
    const inputType = getInputType(file);

    captureEvent(EventName.mw_sts_generation_started, {
      voice_id: voiceId,
      input_type: inputType,
    });

    setPlayingState("loading");
    try {
      await generateSts({
        audioElement: audioElementRef.current,
        config: { referenceAudio: file },
        voiceId,
        modelId: "eleven_multilingual_sts_v2",
      });
      captureEvent(EventName.mw_sts_generation_completed, {
        voice_id: voiceId,
        input_type: inputType,
      });
      setPlayingState("paused");
    } catch (err: unknown) {
      let errorStatus = "unknown";
      if (typeof err === "object" && err !== null && "status" in err) {
        errorStatus = (err as { status: string }).status;
      }
      captureEvent(EventName.mw_sts_generation_error, {
        voice_id: voiceId,
        input_type: inputType,
        error_status: errorStatus,
      });
      setTimeout(() => {
        setPlayingState("paused");
      }, 1000);
    }
  }

  function handleVoiceSelection(voiceId: string) {
    if (voiceId === selectedVoiceId) return;
    const selectedVoice = voices.find(v => v.voiceId === voiceId);
    captureEvent(EventName.mw_sts_voice_selected, {
      voice_id: voiceId,
      voice_name: selectedVoice?.name || "",
    });
    setSelectedVoiceId(voiceId);
    generate(voiceId);
  }

  function playHandler() {
    if (!audioElementRef.current) return;
    switch (playingState) {
      case "playing":
        captureEvent(EventName.mw_sts_playback_toggled, {
          action: "pause",
        });
        audioElementRef.current.pause();
        break;
      case "paused":
        captureEvent(EventName.mw_sts_playback_toggled, {
          action: "play",
        });
        audioElementRef.current.play();
        break;
    }
  }

  function reset() {
    if (audioElementRef.current === null) return;
    captureEvent(EventName.mw_sts_restart_clicked);
    setView("overview");
    audioRecorderRef.current?.reset();
    audioElementRef.current.autoplay = false;
  }

  return (
    <>
      <audio
        autoPlay
        onEnded={() => setPlayingState("paused")}
        onPause={() => setPlayingState("paused")}
        onPlay={() => setPlayingState("playing")}
        ref={audioElementRef}
      />
      <div className='flex flex-1'>
        <div className='flex flex-1 items-center justify-center gap-8 px-16 md:gap-16'>
          {voices.map(({ name, image, voiceId }, i) => {
            const selected = voiceId === selectedVoiceId;
            return (
              <m.div
                animate={{ opacity: 1, y: 0 }}
                className='mt-32 w-full max-w-[200px]'
                initial={{ opacity: 0, y: 2 }}
                key={voiceId}
                transition={{ duration: 0.8, delay: i * 0.1 }}
              >
                <button
                  aria-label={name}
                  className={cn(
                    "flex h-full w-full flex-col items-center justify-center rounded-xl border py-24 transition-all duration-200 focus:border-darkest focus:outline-darkest disabled:cursor-not-allowed md:p-40",
                    selected ? "scale-105 border-darkest" : "border-default",
                  )}
                  disabled={playingState === "loading" && !selected}
                  onClick={() => handleVoiceSelection(voiceId)}
                  type='button'
                >
                  <Avatar className='border-none md:h-64 md:w-64' image={{ resource: image }} size='md' />
                </button>
                <div
                  className={cn(
                    "flex h-32 items-center justify-center transition-all duration-300 will-change-transform",
                    selected ? "translate-y-8" : "translate-y-0",
                  )}
                >
                  <p className='f-ui-04 text-center md:f-ui-03'>{name}</p>
                </div>
              </m.div>
            );
          })}
        </div>
      </div>
      <MediaProgressBar mediaElement={audioElementRef.current || null} />
      <m.div
        animate={{ opacity: 1 }}
        className='relative flex items-center justify-between gap-8 p-16'
        initial={{ opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.15 }}
      >
        <Button icon={<ChevronLeft16 />} iconPosition={ICON_ALIGN_OPTIONS.BEFORE} onClick={reset} variant='elevated'>
          {t("restart")}
        </Button>
        <Button className='w-40 p-0' disabled={!selectedVoiceId || playingState === "loading"} onClick={playHandler}>
          <m.span animate={{ opacity: 1, scale: 1 }} initial={{ opacity: 0, scale: 0.8 }} key={playingState}>
            {playingStateIcon[playingState]}
          </m.span>
        </Button>
      </m.div>
    </>
  );
}

const playingStateIcon: { [key: string]: React.ReactNode } = {
  playing: <Pause20 className='color-white' />,
  paused: <Play20 className='color-white' />,
  loading: <Spinner20 className='color-white' />,
};
